import { useTableNavigation } from "../table-navigation-context";

interface UseTableCellNavigationParams {
	field?: string;
	dataIndex?: number;
	horizontalGroup?: string[];
	focusSelector: (targetField: string, targetRowIndex: number) => string;
}

export const useTableCellNavigation = ({
	field,
	dataIndex,
	horizontalGroup,
	focusSelector,
}: UseTableCellNavigationParams) => {
	const { allNavigableFields, totalRows } = useTableNavigation();

	const focusCell = (targetField: string, targetRowIndex: number) => {
		const selector = focusSelector(targetField, targetRowIndex);
		let element = document.querySelector(selector);

		if (!element) {
			const selectors = [
				`[data-testid="technician-autocomplete-trigger"][data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
				`input[type="checkbox"][data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
				`input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
			];

			for (const fallbackSelector of selectors) {
				element = document.querySelector(fallbackSelector);
				if (element) break;
			}
		}

		if (element instanceof HTMLElement) {
			element.focus();
		}
	};

	const navigateHorizontally = (direction: "left" | "right", metaKeyPressed: boolean) => {
		if (!field || dataIndex === undefined) return;

		if (metaKeyPressed && horizontalGroup) {
			const currentFieldIndex = horizontalGroup.indexOf(field);
			if (currentFieldIndex === -1) {
				const allFieldsIndex = allNavigableFields.indexOf(field);
				if (allFieldsIndex === -1) return;

				if (direction === "right") {
					focusCell(allNavigableFields[allNavigableFields.length - 1], dataIndex);
				} else {
					focusCell(allNavigableFields[0], dataIndex);
				}
			} else {
				if (direction === "right") {
					const lastField = horizontalGroup[horizontalGroup.length - 1];
					focusCell(lastField, dataIndex);
				} else {
					focusCell(horizontalGroup[0], dataIndex);
				}
			}
		} else {
			const currentFieldIndex = allNavigableFields.indexOf(field);
			if (currentFieldIndex === -1) return;

			const nextFieldIndex = direction === "right" ? currentFieldIndex + 1 : currentFieldIndex - 1;

			if (nextFieldIndex >= 0 && nextFieldIndex < allNavigableFields.length) {
				focusCell(allNavigableFields[nextFieldIndex], dataIndex);
			}
		}
	};

	const navigateVertically = (direction: "up" | "down", metaKeyPressed: boolean) => {
		if (!field || dataIndex === undefined) return;

		if (metaKeyPressed) {
			const targetRow = direction === "down" ? totalRows - 1 : 0;
			if (targetRow >= 0) {
				focusCell(field, targetRow);
			}
		} else {
			const targetRow = direction === "down" ? dataIndex + 1 : dataIndex - 1;
			if (targetRow >= 0 && targetRow < totalRows) {
				focusCell(field, targetRow);
			}
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.defaultPrevented) return;

		const metaKey = e.metaKey || e.ctrlKey;

		switch (e.key) {
			case "ArrowRight":
				e.preventDefault();
				navigateHorizontally("right", metaKey);
				break;
			case "ArrowLeft":
				e.preventDefault();
				navigateHorizontally("left", metaKey);
				break;
			case "ArrowDown":
				e.preventDefault();
				navigateVertically("down", metaKey);
				break;
			case "ArrowUp":
				e.preventDefault();
				navigateVertically("up", metaKey);
				break;
		}
	};

	return {
		handleKeyDown,
		focusCell,
		navigateHorizontally,
		navigateVertically,
	};
};
